import { Divider, Flex, Group, SegmentedControl, Select } from '@byecode/ui'
import { DataSourceType, type FieldProtocol, type IndependentAction } from '@lighthouse/core'
import type { BatchImportFileActionConfig, FlowNode, NodeTypes, VariableTree2 } from '@lighthouse/shared'
import {
    getFormDatasourceOptions,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    SelectDataSource,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { FieldSelect } from '@/components/FieldSelect'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../../styles'

const FILE_IMPORT_TYPE_OPTIONS = [
    {
        label: '直接导入',
        value: 'IMAGES'
    },
    {
        label: '解压导入',
        value: 'ARCHIVE'
    }
]

interface BatchImportFileActionConfigureProps {
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: BatchImportFileActionConfig
    onChange: (value: BatchImportFileActionConfig) => void
}

export const BatchImportFileActionConfigure: React.FC<BatchImportFileActionConfigureProps> = ({
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const normalDataSourceList = useMemo(
        () => dataSourceList.filter(ds => ds.type === DataSourceType.dataSource || ds.type === DataSourceType.systemDataSource),
        [dataSourceList]
    )

    const workflowList = useAtomData(workflowListAtom)
    const { viewDsId = '', formDsId = '', viewType } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }
        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)
                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: allParentNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    const selectedDataSource = useMemo(() => dataSourceList.find(ds => ds.id === value.dataSourceId), [dataSourceList, value.dataSourceId])

    const excludeRichTextFieldOptions = useMemo(() => {
        const schema: Record<string, FieldProtocol> = (selectedDataSource?.schema as Record<string, FieldProtocol>) || {}
        return Object.values(schema)
            .filter(field => field.innerType !== 'RICH_TEXT' && field.innerType !== 'NULL')
            .map(field => ({
                label: field.name,
                value: field.id,
                type: field.type,
                innerType: field.innerType
            }))
    }, [selectedDataSource?.schema])

    const fileFieldOptions = useMemo(() => {
        return excludeRichTextFieldOptions.filter(field => field.type === 'file')
    }, [excludeRichTextFieldOptions])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            <Divider color="var(--color-gray-200)" />

            <Group label="目标导入字段">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>数据表</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectDataSource
                            withinPortal
                            searchable
                            placeholder="请选择"
                            value={value.dataSourceId}
                            dataSourceList={normalDataSourceList}
                            onChange={dataSourceId => {
                                if (dataSourceId === value.dataSourceId) {
                                    return
                                }
                                onChange({ ...value, dataSourceId, attachmentFieldId: '' })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>

                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择附件字段</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <FieldSelect
                            dsId={value.dataSourceId}
                            options={fileFieldOptions}
                            value={value.attachmentFieldId}
                            onChange={attachmentFieldId => onChange({ ...value, attachmentFieldId })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>映射字段</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <FieldSelect
                            dsId={value.dataSourceId}
                            options={excludeRichTextFieldOptions}
                            value={value.mappingFieldId}
                            onChange={mappingFieldId => onChange({ ...value, mappingFieldId })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <SC.Tip>
                    {`文件命名必须为"{编号值}_序号" 或 "{编号值}-序号"，例如"001_1"、"001-1"，序号越小的文件顺序越靠前。若文件命名没有序号，则默认排在同批的最后一位。`}
                </SC.Tip>
            </Group>

            <Divider color="var(--color-gray-200)" />

            <Group
                label={
                    <PopoverQuestion
                        type="fileSource"
                        position="top"
                        renderTarget={(node, ref) => (
                            <Flex gap={16} ref={ref} alignItems="center">
                                <SC.FieldTitle required>导入的文件</SC.FieldTitle>
                                {node}
                            </Flex>
                        )}
                    />
                }
            >
                <VariableSelect
                    width="100%"
                    field={{
                        id: '',
                        dsId: '',
                        type: 'text',
                        name: 'fileSource',
                        innerType: 'TEXT'
                    }}
                    innerTypes={['ARRAY']}
                    placeholder="请选择变量值"
                    dataSourceList={dataSourceList}
                    upstreamOptions={upstreamOptions}
                    pageOptions={isNotAutomation ? pageOptions : undefined}
                    userOption={userOption}
                    viewOptions={viewOptions}
                    formOption={formOption}
                    value={value.fileSource}
                    onChange={fileSource => onChange({ ...value, fileSource })}
                />
            </Group>

            <Divider color="var(--color-gray-200)" />

            <Group label="导入设置">
                <ListItem4ByecodeUi>
                    <PopoverQuestion
                        type="importMode"
                        position="top"
                        renderTarget={(node, ref) => (
                            <SC.FormItemLabelWrapper ref={ref}>
                                <SC.FormItemLabel>当导入压缩包时</SC.FormItemLabel>
                                {node}
                            </SC.FormItemLabelWrapper>
                        )}
                    />
                    <SC.FormItemContent>
                        <SegmentedControl
                            style={{ width: 180 }}
                            size="xs"
                            fullWidth
                            labelStyle={{ overflow: 'visible' }}
                            value={value.importMode}
                            data={FILE_IMPORT_TYPE_OPTIONS}
                            onChange={importMode => onChange({ ...value, importMode } as BatchImportFileActionConfig)}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <SC.Tip>
                    支持zip、rar格式的压缩包解压，将上传的文件放于1个文件夹(请勿创建子文件夹)后压缩为zip或rar格式上传。非zip、rar格式的压缩包会直接导入。
                </SC.Tip>
            </Group>
        </SC.Container>
    )
}
